import CloseIcon from '@assets/icons/close.svg';
import i18n from '@/utils/i18n';
import React from 'react';
import { BlurView } from 'expo-blur';
import { createStyles } from '@/contexts/theme-context';
import {
  Pressable,
  Modal as ReactNativeModal,
  StyleSheet,
  View,
} from 'react-native';
import { shadow } from '@/styles';

const DIM_BACKDROP_COLOR = 'rgba(0, 0, 0, 0.1)';

interface ModalProps {
  children: React.ReactNode;
  dismissOnBackdropPress?: boolean;
  headerElement?: React.ReactNode;
  height?: number;
  isOpen: boolean;
  onClose: () => void;
  showCloseButton?: boolean;
  width?: number;
}

export const Modal: React.FC<ModalProps> = ({
  children,
  dismissOnBackdropPress = true,
  headerElement,
  height,
  isOpen,
  onClose,
  showCloseButton = true,
  width,
}) => {
  const [styles] = useStyles();

  const pointerEvents = isOpen ? 'auto' : 'none';

  const renderBackdrop = () => {
    const handleBackdropPress = dismissOnBackdropPress ? onClose : undefined;

    return (
      <BlurView
        experimentalBlurMethod="dimezisBlurView"
        intensity={20}
        style={styles.blurView}
        tint="light"
      >
        <View style={styles.dimOverlay}>
          <Pressable
            onPress={handleBackdropPress}
            style={styles.backdropPress}
          />
        </View>
      </BlurView>
    );
  };

  if (!isOpen) {
    return null;
  }

  return (
    <ReactNativeModal onRequestClose={onClose} transparent visible={isOpen}>
      <View pointerEvents={pointerEvents} style={styles.container}>
        {renderBackdrop()}
        <View style={[styles.modal, { height, width }]}>
          <View style={styles.header}>
            {headerElement}
            {showCloseButton ? (
              <Pressable
                accessibilityLabel={i18n.t('close')}
                onPress={onClose}
                style={styles.closeButton}
              >
                <CloseIcon
                  fill={styles.closeIcon.color}
                  height={24}
                  width={24}
                />
              </Pressable>
            ) : null}
          </View>
          {children}
        </View>
      </View>
    </ReactNativeModal>
  );
};

const useStyles = createStyles((theme) => ({
  backdropPress: {
    flex: 1,
  },
  blurView: {
    ...StyleSheet.absoluteFillObject,
    flex: 1,
  },
  closeButton: {
    alignItems: 'center',
    justifyContent: 'center',
  },
  closeIcon: {
    color: theme.sk.color.icon.primary,
  },
  container: {
    ...StyleSheet.absoluteFillObject,
    alignItems: 'center',
    flex: 1,
    justifyContent: 'center',
    overflow: 'hidden',
    zIndex: 100,
  },
  dimOverlay: {
    ...StyleSheet.absoluteFillObject,
    backgroundColor: DIM_BACKDROP_COLOR,
  },
  header: {
    alignItems: 'center',
    flexDirection: 'row',
    justifyContent: 'flex-end',
    marginBottom: theme.sk.spacing.spaceMedium,
  },
  modal: {
    backgroundColor: theme.sk.color.bg.primary.default,
    borderColor: theme.sk.color.border.primary,
    borderRadius: theme.sk.radii.radiusMedium,
    borderWidth: 1,
    paddingHorizontal: theme.sk.spacing.spaceLarge,
    paddingVertical: theme.sk.spacing.spaceMedium,
    ...shadow({
      opacity: 0.08,
      radius: 24,
      x: 0,
      y: 12,
    }),
  },
}));
